import Swiper from 'swiper';
import {Navigation, Pagination} from 'swiper/modules';

export default class ProductSwiper {
    constructor() {
        this.mainSwiper = null;
        this.thumbs = null;
        this.resizeTimeout = null;
        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.delayedInit();
            });
        } else {
            this.delayedInit();
        }
    }

    delayedInit() {
        // Small delay to ensure all elements are rendered
        setTimeout(() => {
            this.initSwipers();
            this.setupResizeListener();
        }, 100);
    }

    initSwipers() {
        try {
            this.thumbs = document.querySelector('.product-thumbs');
            const mainSlider = document.querySelector('.swiper-main');

            // Exit if main slider doesn't exist
            if (!mainSlider) {
                console.log('Product swiper: Main slider not found on this page');
                return;
            }

            // Check if swiper-wrapper exists
            const mainWrapper = mainSlider.querySelector('.swiper-wrapper');
            if (!mainWrapper) {
                console.error('Product swiper: Main swiper-wrapper not found');
                return;
            }

            this.mainSwiper = new Swiper(mainSlider, {
                modules: [Navigation, Pagination],
                spaceBetween: 0,
                slidesPerView: 1,
                autoHeight: true,
                loop: false,
                observer: false,
                observeParents: false,
                observeSlideChildren: false,
                updateOnWindowResize: false,
                resizeObserver: false,
                watchOverflow: true,
                navigation: {
                    enabled: false
                },
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true,
                },
                on: {
                    init: () => {
                        this.syncActiveThumb();
                    },
                    slideChange: () => {
                        this.syncActiveThumb();
                    },
                },
            });

            this.bindThumbs();

            // Listen for variant changes
            this.setupVariantChangeListener();
        } catch (error) {
            console.error('Error initializing product swiper:', error);
        }
    }

    setupVariantChangeListener() {
        // Listen for Shopify's variant:change event
        document.addEventListener('variant:change', (event) => {
            if (!this.mainSwiper || !event.detail) return;

            const variant = event.detail.variant;
            if (!variant || !variant.featured_media) return;

            this.switchToMedia(variant.featured_media.id);
        });

        // Also listen for custom events from variant selectors
        const variantSelects = document.querySelector('variant-selects');
        if (variantSelects) {
            variantSelects.addEventListener('change', () => {
                const selectedVariantInput = document.querySelector('[name="id"]');
                if (!selectedVariantInput) return;

                // Get variant data from the variant-selects element
                const variantData = variantSelects.currentVariant;
                if (variantData && variantData.featured_media) {
                    this.switchToMedia(variantData.featured_media.id);
                }
            });
        }
    }

    bindThumbs() {
        if (!this.thumbs) return;

        this.thumbs.addEventListener('click', (event) => {
            const item = event.target.closest('.product-thumbs__item');
            if (!item) return;
            this.switchToMedia(item.getAttribute('data-media-id'));
        });
    }

    syncActiveThumb() {
        if (!this.thumbs || !this.mainSwiper) return;

        const activeSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex];
        const mediaId = activeSlide ? activeSlide.getAttribute('data-media-id') : null;

        this.thumbs.querySelectorAll('.product-thumbs__item').forEach((item) => {
            item.classList.toggle('is-active', item.getAttribute('data-media-id') === mediaId);
        });

        this.scrollActiveThumbIntoView();
    }

    scrollActiveThumbIntoView() {
        if (!this.thumbs) return;

        const active = this.thumbs.querySelector('.product-thumbs__item.is-active');
        if (!active) return;
        if (this.thumbs.scrollWidth <= this.thumbs.clientWidth) return;

        const containerRect = this.thumbs.getBoundingClientRect();
        const itemRect = active.getBoundingClientRect();
        const itemCenter = itemRect.left - containerRect.left + this.thumbs.scrollLeft + itemRect.width / 2;
        const maxScroll = this.thumbs.scrollWidth - this.thumbs.clientWidth;
        const left = Math.max(0, Math.min(itemCenter - this.thumbs.clientWidth / 2, maxScroll));

        this.thumbs.scrollTo({
            left,
            behavior: 'smooth',
        });
    }

    switchToMedia(mediaId) {
        if (!this.mainSwiper) return;

        const slides = this.mainSwiper.slides;
        let targetIndex = -1;

        for (let i = 0; i < slides.length; i++) {
            const slideMediaId = slides[i].getAttribute('data-media-id');
            if (slideMediaId && slideMediaId === String(mediaId)) {
                targetIndex = i;
                break;
            }
        }

        if (targetIndex >= 0 && targetIndex !== this.mainSwiper.activeIndex) {
            this.mainSwiper.slideTo(targetIndex, 300);
        }

        this.syncActiveThumb();
    }

    setupResizeListener() {
        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                if (this.mainSwiper) {
                    this.mainSwiper.update();
                }
            }, 500);
        });
    }

    destroy() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        if (this.mainSwiper) {
            this.mainSwiper.destroy(true, true);
            this.mainSwiper = null;
        }
        this.thumbs = null;
    }
}

// Auto-initialize
if (typeof window !== 'undefined') {
    window.productSwiper = new ProductSwiper();

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
        if (window.productSwiper) {
            window.productSwiper.destroy();
        }
    });
}
